import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  HeartPulse,
  ShieldCheck,
  Stethoscope,
  Building2,
  HandCoins,
  Network,
  Lock,
  Globe2,
  FileSearch,
  ClipboardCheck,
  Activity,
  Workflow,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: HeartPulse,
    title: 'WebCenter Imaging as the invoice repository',
    detail:
      'US and AU health systems on 11g and 12c typically run WebCenter Content / Imaging as the invoice repository of record — long-retention, audit-trailed, integrated with the AP workflow. The retention story is part of the HIPAA posture, not a side concern.',
  },
  {
    icon: FileSearch,
    title: 'WFR for supplier extraction',
    detail:
      'WebCenter Forms Recognition handles supplier-specific extraction patterns — invoice numbers, line-item coding hints, pharmacy and medical-supplier formats. The trained template library is a real asset and a real migration consideration.',
  },
  {
    icon: Workflow,
    title: 'SOA Suite for AP workflow approval',
    detail:
      'SOA composites route invoices through department, hospital-entity, and finance approval chains. Healthcare AP workflows often encode entity-specific signoff rules (hospital vs clinic vs foundation) that need to come forward into whatever the next architecture is.',
  },
  {
    icon: ClipboardCheck,
    title: 'ADF non-PO coding for fund and charge account',
    detail:
      'Non-PO coding screens (ADF) handle fund, charge account, project, and grant assignments — a meaningful piece of healthcare AP, particularly for foundation and grant-funded spend. Coding rules tend to be customer-specific and need explicit migration treatment.',
  },
];

const SCENARIOS = [
  {
    label: 'Path A',
    title: 'Lift-and-shift to OCI Marketplace',
    body: 'Works for healthcare shops with a stable supplier set, limited customization, and an AP team comfortable with the existing WebCenter UX. Preserves the architecture, modernizes the infrastructure, runs in-region (US or AU) on OCI. Lowest-disruption forward path; same lifecycle decision recurs when 14c support windows close.',
  },
  {
    label: 'Path A+',
    title: '12c to 14c upgrade',
    body: 'Works for shops with heavy WebCenter customization, a strong internal WebCenter operations team, and a clear preference to extend the existing investment. Out-of-place domain directory upgrade preserves WFR templates, SOA composites, FIPSA accelerators, and ADF customizations. Buys approximately 8 years of supported runway through 2030 / 2033.',
  },
  {
    label: 'Path B',
    title: 'Modernize off WebCenter to AI-driven AP automation',
    body: 'Works for shops that want to consolidate the AP stack, reduce WebCenter operations burden, and move to AI-native extraction with continuous learning. Oracle EBS or Fusion stays as the system of record; the AP automation layer is replaced. One-time strategic decision; no further WebCenter lifecycle cycles to plan around.',
  },
];

const MEASURED_BY_AP = [
  {
    icon: Activity,
    title: 'First-pass straight-through rate',
    detail:
      'What percentage of supplier invoices flow from capture to posted in EBS / Fusion without a human touch. Healthcare AP teams running WFR with mature template libraries typically have a strong first-pass story; preserving it through migration is non-negotiable.',
  },
  {
    icon: Network,
    title: 'Supplier-email exception triage',
    detail:
      'The volume of supplier email handling that sits on top of the automated extraction layer — payment status questions, remittance requests, statement reconciliations, dispute correspondence. The FTE cost here is real and often hidden in the AP headcount line.',
  },
  {
    icon: Lock,
    title: 'HIPAA-compliant audit trail',
    detail:
      'Every invoice, every approval, every coding change, every payment action — captured with user, timestamp, and reason where applicable. Audit trail preservation through any architecture change is a HIPAA-posture item, not a nice-to-have.',
  },
  {
    icon: Building2,
    title: 'Multi-entity coding accuracy',
    detail:
      'Hospitals, clinics, foundations, ambulatory surgery centers, and physician groups often share a single AP team. Coding-rule accuracy across entities — including foundation / charitable arms with separate ledgers — is what defines a healthy month-end close.',
  },
];

const HEALTHCARE_PATTERNS = [
  {
    icon: Building2,
    title: 'Multi-hospital entity coding inside a single AP team',
    detail:
      'A central AP team services multiple hospital entities, each with its own coding structures, approval matrices, and reporting requirements. WebCenter SOA composites encode the entity-routing logic; the trained WFR templates handle entity-specific supplier formats. Migration treatment for entity rules is the load-bearing piece.',
  },
  {
    icon: HandCoins,
    title: 'Foundation / charitable arm separate ledger',
    detail:
      'Health system foundations frequently run a separate general ledger from the operating health system, with restricted-fund coding and donor-restricted spend rules. AP automation that respects this separation — and the approval rules that go with restricted funds — is required, not optional.',
  },
  {
    icon: Stethoscope,
    title: 'Patient accounting separate from supplier AP',
    detail:
      'Patient accounts receivable, billing, and revenue cycle systems sit on a different stack (often Epic or Cerner) from supplier AP. The boundary is well-defined: WebCenter AP handles supplier invoices going out to the GL; patient accounting handles patient billing on the revenue side. Modernization preserves the boundary.',
  },
  {
    icon: ShieldCheck,
    title: 'Health plan (payer) integration with provider AP',
    detail:
      'Integrated delivery networks running provider and payer arms have AP flows that touch both sides — provider supplier AP on one ledger, plan operations AP on another. Coding and approval routing reflect the organizational separation; the architecture has to as well.',
  },
];

const HIPAA_POSTURE = [
  {
    icon: Globe2,
    title: 'In-region OCI deployment',
    detail:
      'US health systems deploy in us-east or us-west (Phoenix, Ashburn, Chicago); AU health systems deploy in ap-southeast-2 (Sydney). Data residency stays inside the contracted region. No cross-region replication without an explicit BAA-compatible architecture decision.',
  },
  {
    icon: ShieldCheck,
    title: 'SOC 2 Type II',
    detail:
      'Control environment and audit evidence aligned to SOC 2 Type II. The control set covers access management, change management, encryption, monitoring, and incident response — the same control surface AP and IT compliance teams ask about in vendor reviews.',
  },
  {
    icon: Lock,
    title: '256-bit AES encryption',
    detail:
      'Encryption at rest and in transit. Invoice images, extracted data, audit trail records, and supplier correspondence are encrypted end to end. Key management aligned to OCI Key Management Service / Oracle Vault patterns.',
  },
  {
    icon: ClipboardCheck,
    title: 'Signed BAAs and audit trail preservation',
    detail:
      'Business Associate Agreement executed before any PHI-adjacent data movement. Migration plans explicitly preserve the WebCenter audit trail forward into the new architecture so the HIPAA-posture story stays intact across the cutover, not reset by it.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    label: 'Tier 1 · Healthcare WebCenter Assessment',
    title: 'Fixed-fee inventory and forward plan',
    body: 'Andrew reads your existing healthcare WebCenter AP implementation directly — Imaging configuration, WFR template library, SOA composites for entity routing, ADF non-PO coding customizations, EBS or Fusion integration. Delivers a structured forward plan covering Path A, A+, and B options in the context of your specific multi-entity setup. Plan is yours regardless of delivery partner.',
  },
  {
    label: 'Tier 2 · Architecture Review',
    title: 'One-week deep read of the running system',
    body: 'A one-week fixed-fee read of your running healthcare WebCenter — entity routing rules, WFR rules, SOA composites, FIPSA accelerators, ADF customizations, EBS / Fusion integration, audit trail design. Output is a structured inventory used as input to any 14c upgrade, OCI Marketplace lift, or modernization decision.',
  },
  {
    label: 'Tier 3 · Strategic Advisory Retainer',
    title: 'Founder-level guidance through the decision window',
    body: 'Monthly retainer with Andrew sized for healthcare CFO / Controller / AP leadership working a 6-12 month decision with internal stakeholders, procurement, IT compliance, and Oracle. Particularly useful where the forward path interacts with Epic / Cerner integration boundaries or a planned Oracle Fusion ERP migration.',
  },
  {
    label: 'Tier 4 · Forward Deployed Engineering',
    title: 'Embedded engineer for the modernization itself',
    body: 'Forward Deployed Engineering option for healthcare shops choosing Path B. An embedded engineer with founder-level WebCenter and healthcare-AP context delivering the modernization end-to-end — entity-routing logic, multi-fund coding rules, audit trail continuity, and EBS or Fusion integration. Scoped to a defined outcome with a named go-live.',
  },
];

const FAQS = [
  {
    q: 'Is EZ Cloud HIPAA-compliant and will you sign a BAA?',
    a: 'Yes — EZ Cloud operates with HIPAA-compliant deployment patterns and signs Business Associate Agreements with covered entities and business associates before any PHI-adjacent data movement. The deployment pattern covers in-region OCI deployment, 256-bit AES encryption at rest and in transit, SOC 2 Type II aligned control environment, and full audit trail. The BAA is executed as part of the contracting process and reviewed alongside the standard MSA.',
  },
  {
    q: 'Can EZ Cloud guarantee in-region data residency for US and AU healthcare customers?',
    a: 'Yes — US healthcare customers deploy in US OCI regions (us-east Ashburn / Chicago or us-west Phoenix) and AU healthcare customers deploy in ap-southeast-2 (Sydney), with customer data staying inside the contracted region. This includes invoice images, extracted data, audit trail, and supplier correspondence. Cross-region replication, when used for resilience, is constrained to a customer-approved region pairing — for AU customers this is typically ap-southeast-2 to ap-southeast-1 or kept fully in-region per customer preference.',
  },
  {
    q: 'What happens to the WebCenter audit trail through migration?',
    a: 'The audit trail is treated as a first-class migration artifact, preserved natively for Path A and A+ via the same UCM / Imaging metadata model carried forward, and exported with chain-of-custody documentation into the modernized platform for Path B. For Path B, new audit records continue forward in the modernized platform after cutover. HIPAA-posture continuity through cutover is an explicit design item, not a side effect.',
  },
  {
    q: 'How does EZ Cloud handle multi-entity coding for a central AP team?',
    a: 'EZ Cloud encodes entity-routing logic explicitly so a central AP team operates against a single workflow with entity-aware coding suggestions, entity-specific approval chains, and entity-segregated reporting. Hospitals, clinics, foundations, ambulatory surgery centers, and physician groups each have their own coding structures, approval matrices, and routing rules, and multi-entity coding is treated as a first-class healthcare pattern. Foundation / charitable-arm restricted-fund coding is supported as its own pattern.',
  },
  {
    q: 'How does EZ Cloud integrate with Epic, Cerner, and Workday HCM?',
    a: 'EZ Cloud sits in the supplier AP lane and integrates natively with Oracle EBS Payables and Oracle Fusion Cloud Payables, well-separated from patient accounting (Epic / Cerner) and HCM (Workday). Where data does cross the boundary — for example, employee expense feeds from Workday HCM into AP, or supplier setup synchronization with a clinical system — the integration is handled through Oracle Integration Cloud or the customer-preferred middleware. The integration boundary is documented and approved as part of the assessment phase.',
  },
  {
    q: 'How long does a typical healthcare WebCenter migration take?',
    a: 'Typical timelines range 4-9 months for Path B (modernization) and 6-12 months for Path A+ (14c upgrade). Healthcare-specific scope items extend the longer end — multi-entity coding rules, foundation ledger separation, audit trail continuity, and integration testing with clinical-adjacent systems. The Inventory & Assessment phase typically runs 2-4 weeks and produces the right timeline estimate for the specific implementation rather than a generic range.',
  },
  {
    q: 'What are the staffing and talent considerations on the healthcare side?',
    a: 'On the customer side, the engineers who originally built the SOA composites, WFR templates, and ADF customizations may no longer be available, so the practical question is how to carry that knowledge forward without a dedicated WebCenter operations bench. EZ Cloud engagements are scoped so customer staffing focuses on AP-process and compliance-review work rather than WebCenter-specific operations work, and the assessment phase documents what knowledge needs to come forward to the customer team versus what is held by EZ Cloud post go-live.',
  },
  {
    q: 'How is engagement pricing established?',
    a: 'Engagement pricing is established per situation on a Decision Call. Healthcare scope items — multi-entity setup, foundation ledger, BAA execution, audit trail continuity, in-region OCI architecture, and any clinical-adjacent integration — are surfaced explicitly during the assessment phase so the pricing reflects the actual scope rather than a generic template. Fixed-fee assessments, project-based modernization work, and Forward Deployed Engineering arrangements are all available depending on the customer preference.',
  },
];

const WebCenterHealthcare = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle WebCenter for Healthcare — HIPAA-Compliant AP Modernization on Oracle EBS and Fusion | EZ Cloud',
    description:
      'Oracle WebCenter AP modernization for healthcare systems. HIPAA-compliant deployment patterns, in-region OCI residency, integration with Oracle EBS Payables and Oracle Fusion Cloud Payables. The December 2026 12c support cliff and how healthcare AP teams plan the forward path without disrupting close cycles.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-healthcare',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter Healthcare' },
      { '@type': 'Thing', name: 'Healthcare AP Automation' },
      { '@type': 'Thing', name: 'HIPAA Compliance' },
      { '@type': 'Thing', name: 'Oracle EBS Healthcare' },
      { '@type': 'Thing', name: 'Oracle Fusion Cloud Healthcare' },
      { '@type': 'Thing', name: 'Accounts Payable Automation' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter for Healthcare — HIPAA-Compliant AP Modernization on Oracle EBS and Fusion | EZ Cloud"
        description="Oracle WebCenter AP modernization for healthcare systems. HIPAA-compliant deployment patterns, in-region OCI residency, integration with Oracle EBS Payables and Oracle Fusion Cloud Payables. The December 2026 12c support cliff and how healthcare AP teams plan the forward path without disrupting close cycles."
        canonical="https://ezcloud.ai/solutions/webcenter-healthcare"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter for Healthcare',
            url: 'https://ezcloud.ai/solutions/webcenter-healthcare',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <HeartPulse className="w-4 h-4" aria-hidden />
              Healthcare · Oracle WebCenter AP · December 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter for healthcare AP
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                HIPAA-compliant modernization for healthcare systems on Oracle EBS and Fusion.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              The December 2026 12c support cliff, the forward paths, and what changes for
              healthcare AP teams running WebCenter Imaging, WFR, SOA Suite, and ADF non-PO coding.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-healthcare-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Healthcare WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              WebCenter AP modernization for healthcare, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  WebCenter AP modernization for healthcare systems on Oracle EBS or Oracle Fusion
                  Cloud — HIPAA-compliant deployment patterns, in-region OCI residency, and audit
                  trail preservation across the cutover.
                </strong>{' '}
                The forward path is shaped around how a central AP team services multiple hospital,
                clinic, and foundation entities while staying inside the compliance posture the IT
                and audit teams have already established.
              </p>
              <p>
                Oracle Fusion Middleware 12c — covering WebCenter Content, Imaging, Forms
                Recognition, Enterprise Capture, and the SOA Suite that drives AP workflow — exits
                Premier Support in <strong className="text-gray-900">December 2026</strong>. The
                decision window for healthcare AP teams is now.
              </p>
              <p>
                Three forward paths apply, with a healthcare-specific lens on each. Internal
                references: the broader{' '}
                <Link
                  to="/solutions/modernize-oracle-webcenter"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  WebCenter modernization framework
                </Link>
                , the{' '}
                <Link
                  to="/solutions/webcenter-12c-end-of-support-decision"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  12c end-of-support decision page
                </Link>
                , the{' '}
                <Link
                  to="/solutions/healthcare-oracle-fusion"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  Healthcare on Oracle Fusion
                </Link>{' '}
                solution page, and the{' '}
                <Link
                  to="/industries/healthcare"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  Healthcare industry page
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Where healthcare customers are today */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The current state
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where healthcare WebCenter AP customers are today
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              11g and 12c installs across US and AU healthcare systems. The shape of the stack is
              consistent enough across customers that the migration considerations are well-known —
              the specifics matter, and they vary by customer.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What healthcare AP teams measure */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What healthcare AP teams measure
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four metrics that define a healthy healthcare AP function
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              First-pass straight-through, supplier-email exception triage, HIPAA-compliant audit
              trail, and multi-entity coding accuracy. Forward-path decisions are graded against
              what they do to these four lines.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {MEASURED_BY_AP.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* The December 2026 decision for healthcare */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The forward paths
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The December 2026 decision for healthcare
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Three forward paths, each viewed through a healthcare-specific lens. There is no
              single right answer — the right answer is a function of customization depth, internal
              WebCenter operations strength, AP stack consolidation goals, and the integration
              boundary with the clinical systems.
            </p>
            <div className="space-y-5">
              {SCENARIOS.map((s) => (
                <Card key={s.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div className="flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide bg-accent/10 text-accent border border-accent/30">
                      {s.label}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-600 mt-8">
              Want the full four-path framework?{' '}
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                See the 12c end-of-support decision page
                <ArrowRight className="w-3.5 h-3.5 ml-1 inline" aria-hidden />
              </Link>
            </p>
          </div>
        </section>

        {/* HIPAA + compliance posture */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              HIPAA + compliance posture
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              In-region residency, signed BAAs, audit trail continuity
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The HIPAA posture is not a checklist at the end — it is the architecture. In-region
              OCI deployment, SOC 2 Type II aligned control environment, 256-bit AES encryption,
              signed BAAs, and audit trail preservation through migration are the load-bearing
              pieces.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {HIPAA_POSTURE.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Healthcare-specific patterns */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Healthcare-specific patterns we see
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four patterns that shape healthcare AP architecture
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Multi-entity coding, foundation ledger separation, patient-accounting boundary, and
              payer integration. Each pattern is real, each is encoded somewhere in the existing
              WebCenter implementation, and each needs an explicit forward decision.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {HEALTHCARE_PATTERNS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Customer reference */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Customer reference
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Healthcare customer on Oracle Fusion — Barwon Health
            </h2>
            <Card className="p-8 bg-white border border-gray-200 shadow-sm">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="w-14 h-14 rounded-lg bg-accent/15 flex items-center justify-center flex-shrink-0">
                  <Stethoscope className="w-7 h-7 text-accent" aria-hidden />
                </div>
                <div className="flex-1">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Barwon Health is a healthcare customer running EZ Cloud on Oracle Fusion Cloud
                    ERP, integrated via Oracle Integration Cloud (OIC), deployed in the
                    ap-southeast-2 (Sydney) OCI region for in-region data residency. The AP
                    automation layer handles supplier invoices end-to-end into Fusion Payables;
                    coding, approval routing, and audit trail are encoded to the customer's
                    multi-entity setup.
                  </p>
                  <Link
                    to="/customers/health-system-oracle-fusion"
                    className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
                  >
                    Read the healthcare on Oracle Fusion case study
                    <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Engagement options */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Engagement options
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for healthcare WebCenter
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same 25-year founder-level WebCenter expertise applied through the healthcare
              compliance lens. Standard consulting tiers and a Forward Deployed Engineering option
              for shops choosing the modernization path.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.label}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, with stack-specific considerations,
              eight diagnostic questions, and a vendor evaluation checklist. Applies directly to the
              healthcare decision frame.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on healthcare WebCenter AP modernization
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your healthcare WebCenter decision
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist. Walk through your multi-entity setup, HIPAA posture,
              integration boundary with the clinical systems, and the forward path in the context
              of your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-healthcare-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Healthcare WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterHealthcare;
